/**
 * Derived resource readings. Hosts report cumulative kernel counters; everything that needs
 * two readings (rates, latency, per-core use) is computed here so every client agrees.
 */
import type {
  ServerDisk,
  ServerHistory,
  ServerInterface,
  ServerProcesses,
  ServerResources,
} from '@atum-remote/protocol/server-inspection';

export type Severity = 'warning' | 'critical';
export interface Finding {
  readonly id: string;
  readonly severity: Severity;
  readonly title: string;
  readonly detail: string;
}

/** Seconds between two readings on the host's own clock, or null when not comparable. */
export function elapsed(before: ServerResources | undefined, after: ServerResources) {
  if (before?.uptime == null || after.uptime == null) return null;
  const seconds = after.uptime - before.uptime;
  return seconds > 0.5 && seconds < 600 ? seconds : null;
}

function delta(before: number | undefined, after: number | undefined) {
  return before == null || after == null || after < before ? null : after - before;
}

export function perCoreUsage(before: ServerResources | undefined, after: ServerResources) {
  const a = before?.cpu?.perCore,
    b = after.cpu?.perCore;
  if (!a || !b || a.length !== b.length) return null;
  return b.map(([total, idle], i) => {
    const t = total - a[i]![0],
      d = idle - a[i]![1];
    return t > 0 && d >= 0 && d <= t ? (100 * (t - d)) / t : null;
  });
}

export function iowaitPercent(before: ServerResources | undefined, after: ServerResources) {
  const total = delta(before?.cpu?.total, after.cpu?.total);
  const wait = delta(before?.cpu?.iowait, after.cpu?.iowait);
  return total && wait != null && wait <= total ? (100 * wait) / total : null;
}

export interface MemoryBreakdown {
  total: number;
  /** Memory that cannot be reclaimed without swapping: total minus MemAvailable. */
  used: number;
  available: number;
  /** Page cache and reclaimable slab: in use, but given back on demand. */
  cache: number;
  free: number;
  percent: number;
  swapTotal: number | null;
  swapUsed: number | null;
}

export function memoryBreakdown(memory: Record<string, number>): MemoryBreakdown | null {
  const total = memory.MemTotal,
    available = memory.MemAvailable;
  if (!total || available == null || available > total) return null;
  const free = Math.min(memory.MemFree ?? 0, available);
  const reported =
    (memory.Buffers ?? 0) + (memory.Cached ?? 0) + (memory.SReclaimable ?? 0) - (memory.Shmem ?? 0);
  // Available is free memory plus what the kernel expects to reclaim; never show more cache
  // than that, so the three segments always add up to the total.
  const cache = Math.max(0, Math.min(reported, available - free));
  const swapTotal = memory.SwapTotal ?? null;
  return {
    total,
    used: total - available,
    available,
    cache,
    free: available - cache,
    percent: (100 * (total - available)) / total,
    swapTotal,
    swapUsed:
      swapTotal != null && memory.SwapFree != null
        ? Math.max(0, swapTotal - memory.SwapFree)
        : null,
  };
}

/** Pages swapped in and out per second between readings. */
export function swapRate(before: ServerResources | undefined, after: ServerResources) {
  const seconds = elapsed(before, after);
  const inbound = delta(before?.vm?.swapIn, after.vm?.swapIn);
  const outbound = delta(before?.vm?.swapOut, after.vm?.swapOut);
  return seconds && inbound != null && outbound != null ? (inbound + outbound) / seconds : null;
}

export interface DiskRates {
  readBytes: number;
  writeBytes: number;
  readIops: number;
  writeIops: number;
  /** Average milliseconds per completed request (Linux "await"). */
  readLatency: number | null;
  writeLatency: number | null;
  /** Share of wall time with I/O in flight; can read 100% on parallel NVMe while healthy. */
  busy: number;
}

export function diskRates(
  before: ServerResources | undefined,
  after: ServerResources,
): Map<string, DiskRates> {
  const seconds = elapsed(before, after);
  const rates = new Map<string, DiskRates>();
  if (!seconds) return rates;
  const previous = new Map(before?.disks?.map((d) => [d.name, d]));
  for (const disk of after.disks ?? []) {
    const old = previous.get(disk.name);
    if (!old) continue;
    const reads = delta(old.reads, disk.reads),
      writes = delta(old.writes, disk.writes),
      readSectors = delta(old.readSectors, disk.readSectors),
      writeSectors = delta(old.writeSectors, disk.writeSectors),
      readMs = delta(old.readMs, disk.readMs),
      writeMs = delta(old.writeMs, disk.writeMs),
      busyMs = delta(old.busyMs, disk.busyMs);
    if ([reads, writes, readSectors, writeSectors, readMs, writeMs, busyMs].includes(null))
      continue;
    rates.set(disk.name, {
      readBytes: (readSectors! * 512) / seconds,
      writeBytes: (writeSectors! * 512) / seconds,
      readIops: reads! / seconds,
      writeIops: writes! / seconds,
      readLatency: reads ? readMs! / reads : null,
      writeLatency: writes ? writeMs! / writes : null,
      busy: Math.min(100, busyMs! / (seconds * 10)),
    });
  }
  return rates;
}

export function interfaceRates(before: ServerResources | undefined, after: ServerResources) {
  const seconds = elapsed(before, after);
  const rates = new Map<string, { received: number; sent: number }>();
  if (!seconds) return rates;
  const previous = new Map(before?.network.map((n) => [n.name, n]));
  for (const item of after.network) {
    const old = previous.get(item.name);
    const received = delta(old?.received, item.received),
      sent = delta(old?.sent, item.sent);
    if (received != null && sent != null)
      rates.set(item.name, { received: received / seconds, sent: sent / seconds });
  }
  return rates;
}

/** Interfaces carrying the host's own traffic, so bridges and veths are not double counted. */
export function isHostTraffic(item: ServerInterface, all: readonly ServerInterface[]) {
  if (item.kind) {
    // A bond carries its members' traffic; count the bond, not each member again.
    if (item.master && all.some((i) => i.name === item.master && i.kind === 'bond')) return false;
    if (['physical', 'wireless', 'bond'].includes(item.kind)) return true;
    return (
      !!item.defaultRoute?.length &&
      !all.some((i) => ['physical', 'wireless', 'bond'].includes(i.kind ?? ''))
    );
  }
  // Older hosts do not classify; exclude loopback and common container plumbing by name.
  return !/^(lo|docker|br-|veth|virbr|cni|flannel|cali|lxc)/.test(item.name);
}

export function isIncidental(item: ServerInterface) {
  return item.kind
    ? ['loopback', 'virtual', 'bridge'].includes(item.kind) && !item.defaultRoute?.length
    : /^(lo|docker|br-|veth|virbr)/.test(item.name);
}

export function filesystemPercent(fs: { used: number; available: number }) {
  // Reserved blocks are not allocatable by the enrolled non-root account, as df reports.
  return fs.used + fs.available > 0 ? (100 * fs.used) / (fs.used + fs.available) : 0;
}

export function processCpu(
  rows: NonNullable<ServerProcesses['processes']>,
  sort: 'cpu' | 'memory',
): NonNullable<ServerProcesses['processes']> {
  return [...rows].sort((a, b) =>
    sort === 'cpu' ? (b.cpu ?? -1) - (a.cpu ?? -1) || b.rss - a.rss : b.rss - a.rss,
  );
}

/** Mean of the finite values in the trailing `count` points, when enough are present. */
export function trailingMean(values: readonly (number | null)[], count: number, minimum: number) {
  const recent = values.slice(-count).filter((v): v is number => v != null);
  return recent.length >= minimum ? recent.reduce((a, b) => a + b, 0) / recent.length : null;
}

export interface HealthInput {
  readonly resources: ServerResources;
  readonly previous?: ServerResources | undefined;
  /** 15-minute host history (5-second steps); other windows are ignored for findings. */
  readonly history?: ServerHistory | undefined;
  /** Disk names whose latency was already high in the previous reading. */
  readonly slowDisks?: ReadonlySet<string> | undefined;
  /** Whether swapping was already active in the previous reading. */
  readonly swapping?: boolean | undefined;
}

const TEMPERATURE_WARNING = 70;

/**
 * Conditions worth an operator's attention. Each rule requires either a sustained window or
 * two consecutive readings, so a single spike never raises a finding. A healthy host yields [].
 */
export function healthFindings(input: HealthInput): Finding[] {
  const { resources, previous } = input;
  const findings: Finding[] = [];
  const cores = resources.cpu?.cores || resources.cpuInfo?.threads || 0;

  // Only the fine 15-minute ring (5 s points) is judged: coarser windows put a partial
  // wall-clock bucket last, where a short spike could pass for a sustained average.
  const recent = input.history?.window === '15m' ? input.history : undefined;
  const cpuHistory = recent ? trailingMean(recent.series.cpu, 60, 48) : null;
  if (cpuHistory != null && cpuHistory >= 90)
    findings.push({
      id: 'cpu',
      severity: cpuHistory >= 98 ? 'critical' : 'warning',
      title: 'Sustained high CPU',
      detail: `Averaged ${cpuHistory.toFixed(0)}% over the last 5 minutes.`,
    });

  const memory = memoryBreakdown(resources.memory);
  const stall = resources.pressure?.memory;
  if (stall) {
    const full = stall.full?.avg60 ?? 0;
    if (full >= 5 || stall.some.avg60 >= 10)
      findings.push({
        id: 'memory',
        severity: full >= 5 ? 'critical' : 'warning',
        title: 'Memory pressure',
        detail: `Tasks waited on memory ${stall.some.avg60.toFixed(1)}% of the last minute${full ? ` (fully stalled ${full.toFixed(1)}%)` : ''}.`,
      });
  } else if (memory && memory.available / memory.total < 0.1) {
    findings.push({
      id: 'memory',
      severity: memory.available / memory.total < 0.05 ? 'critical' : 'warning',
      title: 'Low available memory',
      detail: `${((100 * memory.available) / memory.total).toFixed(1)}% of memory is available, including reclaimable cache.`,
    });
  }

  const swap = swapRate(previous, resources);
  if (swap != null && swap >= 256 && input.swapping)
    findings.push({
      id: 'swap',
      severity: swap >= 4096 ? 'critical' : 'warning',
      title: 'Active swapping',
      detail: `About ${Math.round(swap).toLocaleString()} pages per second moved to or from swap.`,
    });

  for (const fs of resources.filesystems ?? []) {
    const percent = filesystemPercent(fs);
    const inodes = fs.inodesTotal ? 100 * (1 - fs.inodesFree / fs.inodesTotal) : 0;
    if (percent >= 90)
      findings.push({
        id: `fs:${fs.mount}`,
        severity: percent >= 95 ? 'critical' : 'warning',
        title: `${fs.mount} is ${percent.toFixed(0)}% full`,
        detail: `${fs.source} · ${fs.type}`,
      });
    else if (inodes >= 90)
      findings.push({
        id: `inodes:${fs.mount}`,
        severity: inodes >= 95 ? 'critical' : 'warning',
        title: `${fs.mount} is running out of inodes`,
        detail: `${inodes.toFixed(0)}% of inodes used; new files may fail while space remains.`,
      });
  }

  const rates = diskRates(previous, resources);
  for (const disk of resources.disks ?? []) {
    const rate = rates.get(disk.name);
    const latency = Math.max(rate?.readLatency ?? 0, rate?.writeLatency ?? 0);
    if (
      rate &&
      rate.readIops + rate.writeIops >= 10 &&
      latency >= slowLatency(disk) &&
      input.slowDisks?.has(disk.name)
    )
      findings.push({
        id: `latency:${disk.name}`,
        severity: latency >= slowLatency(disk) * 4 ? 'critical' : 'warning',
        title: `High latency on ${disk.name}`,
        detail: `${latency.toFixed(0)} ms per request across consecutive readings.`,
      });
    findings.push(...diskHealthFindings(disk));
  }

  for (const item of resources.network) {
    if (
      ['physical', 'bond'].includes(item.kind ?? '') &&
      item.state === 'down' &&
      (item.defaultRoute?.length || item.addresses?.length)
    )
      findings.push({
        id: `link:${item.name}`,
        severity: item.defaultRoute?.length ? 'critical' : 'warning',
        title: `${item.name} is down`,
        detail: 'The interface has addresses configured but no link.',
      });
  }

  const load5 = resources.load[1];
  if (cores && load5 != null && load5 / cores >= 2)
    findings.push({
      id: 'load',
      severity: load5 / cores >= 4 ? 'critical' : 'warning',
      title: 'High system load',
      detail: `5-minute load ${load5.toFixed(1)} on ${cores} logical CPUs.`,
    });

  return findings.sort(
    (a, b) => Number(b.severity === 'critical') - Number(a.severity === 'critical'),
  );
}

export function slowLatency(disk: Pick<ServerDisk, 'kind'>) {
  return disk.kind === 'hdd' ? 150 : disk.kind === 'virtual' ? 100 : 50;
}

export function diskHealthFindings(disk: ServerDisk): Finding[] {
  const findings: Finding[] = [];
  const health = disk.health;
  const label = disk.model ? `${disk.name} (${disk.model})` : disk.name;
  if (health?.criticalWarnings.length)
    findings.push({
      id: `health:${disk.name}`,
      severity: 'critical',
      title: `${label} reports a critical warning`,
      detail: health.criticalWarnings.map(describeWarning).join(', '),
    });
  if (health?.failing)
    findings.push({
      id: `failing:${disk.name}`,
      severity: 'critical',
      title: `${label} predicts failure`,
      detail: 'SMART reports the drive is failing. Back up and replace it.',
    });
  if (
    health?.availableSpare != null &&
    health.spareThreshold != null &&
    health.availableSpare <= health.spareThreshold
  )
    findings.push({
      id: `spare:${disk.name}`,
      severity: 'critical',
      title: `${label} is out of spare blocks`,
      detail: `Available spare ${health.availableSpare}% at or below its ${health.spareThreshold}% threshold.`,
    });
  if (health?.percentUsed != null && health.percentUsed >= 90)
    findings.push({
      id: `wear:${disk.name}`,
      severity: health.percentUsed >= 100 ? 'critical' : 'warning',
      title: `${label} is ${health.percentUsed}% worn`,
      detail: 'The drive has used most of its rated write endurance.',
    });
  if (health?.mediaErrors)
    findings.push({
      id: `media:${disk.name}`,
      severity: 'warning',
      title: `${label} has media errors`,
      detail: `${health.mediaErrors.toLocaleString()} unrecovered data integrity errors recorded.`,
    });
  if (disk.temperatureC != null && disk.temperatureC >= TEMPERATURE_WARNING)
    findings.push({
      id: `temp:${disk.name}`,
      severity: disk.temperatureC >= TEMPERATURE_WARNING + 10 ? 'critical' : 'warning',
      title: `${label} is running hot`,
      detail: `${disk.temperatureC.toFixed(0)} °C.`,
    });
  return findings;
}

const warningLabels: Record<string, string> = {
  spare: 'spare capacity below threshold',
  temperature: 'temperature outside limits',
  degraded: 'reliability degraded',
  readonly: 'media placed in read-only mode',
  volatile_mem: 'volatile memory backup failed',
  pmr_readonly: 'persistent memory region read-only',
};
export function describeWarning(code: string) {
  return warningLabels[code] ?? code;
}

/** One-word disk health for tables: never green unless the host could actually tell. */
export function diskHealthLabel(disk: ServerDisk): {
  label: string;
  tone: 'ok' | 'warn' | 'danger' | 'muted';
} {
  const findings = diskHealthFindings(disk);
  if (findings.some((f) => f.severity === 'critical')) return { label: 'Failing', tone: 'danger' };
  if (findings.length) return { label: 'Attention', tone: 'warn' };
  if (disk.health) return { label: 'Healthy', tone: 'ok' };
  return { label: 'Not reported', tone: 'muted' };
}

export const healthUnavailableText: Record<string, string> = {
  busctl_unavailable: 'Health requires systemd busctl on the host.',
  udisks_unavailable: 'Health requires the udisks2 service on the host.',
  udisks_nvme_unsupported: 'This udisks2 version does not report NVMe health.',
  udisks_ata_unsupported: 'udisks2 does not report SMART for this drive.',
  udisks_not_collected: 'udisks2 has not collected health data for this drive yet.',
  smart_disabled: 'SMART is disabled on this drive.',
  udisks_timeout: 'udisks2 did not answer in time; health will be retried.',
  udisks_no_drive: 'udisks2 does not associate a physical drive with this device.',
};

export function rate(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const units = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
  let v = value,
    i = 0;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i++;
  }
  return `${v.toLocaleString(undefined, { maximumFractionDigits: v < 10 && i ? 1 : 0 })} ${units[i]}`;
}

export function linkSpeed(mbps: number | null | undefined) {
  if (!mbps) return null;
  return mbps >= 1000 ? `${(mbps / 1000).toLocaleString()} Gb/s` : `${mbps} Mb/s`;
}

export function duration(seconds: number | null | undefined) {
  if (seconds == null) return '—';
  const d = Math.floor(seconds / 86400),
    h = Math.floor(seconds / 3600) % 24,
    m = Math.floor(seconds / 60) % 60;
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m`;
  return `${Math.floor(seconds)}s`;
}

export const processStates: Record<string, string> = {
  R: 'Running',
  S: 'Sleeping',
  D: 'Waiting on I/O',
  Z: 'Zombie',
  T: 'Stopped',
  t: 'Traced',
  I: 'Idle',
  X: 'Dead',
};
